import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { fromEvent, interval, of, Subscription } from 'rxjs';
import { LookupItem, PoliticalInitializationTiers, StringLookupItem } from '@models/lookup';
import {
    getAllDisplayText,
    getDisplayText,
    isDeferrableFilter,
    isGroupMultiSelectFilter,
    isMultiOptionSelected,
    isMultiSelectFilter,
    isSelectFilter,
    segmentTrack,
} from '@shared/helpers/functions/helpers';
import { debounce, map } from 'rxjs/operators';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MAT_SELECT_CONFIG, MatSelect } from '@angular/material/select';
import { Router } from '@angular/router';
import { MultiSelectFilter, SelectFilter } from '@models/filter-types';
import { FilterInputErrorStateMatcher } from '@shared/components/filters/filter-input-error-state-matcher';

const SPACE_KEY_CODE = 'Space';
const TAB_KEY_CODE = 'Tab';
const KEY_A_CODE = 'KeyA';
const SEARCH_DELAY = 500;

export type SupportedTypes = StringLookupItem | LookupItem;

@Component({
    selector: 'app-dropdown-filter',
    templateUrl: './dropdown-filter.component.html',
    styleUrls: ['./dropdown-filter.component.scss'],
    providers: [{ useValue: { overlayPanelClass: 'select-overlay' }, provide: MAT_SELECT_CONFIG }],
    standalone: false,
})
export class DropdownFilterComponent<T extends SupportedTypes> implements OnInit, OnChanges, OnDestroy, AfterViewInit {
    @Input() overrideDisabled!: boolean;
    @Input() preventTab = false;
    @Input() collapsible = false;
    @Input() property!: SelectFilter<T> | MultiSelectFilter<T>;
    @Input() disabledOptions!: T[];
    @Input() hideOptions!: T[];

    @Output() filterRef: EventEmitter<unknown> = new EventEmitter<unknown>();
    @Output() optionDeleteButtonClicked: EventEmitter<unknown> = new EventEmitter<unknown>();
    @Output() valueChanged: EventEmitter<unknown> = new EventEmitter<unknown>();
    @Output() keyDownEvent: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();

    @ViewChild('search') searchTextBox!: ElementRef;
    @ViewChild('selectFilter') selectFilterHTML!: MatSelect;

    disabled = false;

    searchTextboxControl = new UntypedFormControl();
    selectedValues: T[] = [];

    isSearching = false;

    collapsedGroups = new Set<string>();
    private initializedGroups = new Set<string>();

    errorStateMatcher!: FilterInputErrorStateMatcher<T>;

    protected readonly parseFloat = parseFloat;

    private subs: Subscription[] = [];

    constructor(private router: Router) {}

    ngOnInit() {}

    ngOnChanges() {
        this.disabled = this.property?.isReadonly || this.overrideDisabled;
        this.errorStateMatcher = new FilterInputErrorStateMatcher<T>(this.property);
    }

    ngAfterViewInit(): void {
        if (this.selectFilterHTML) {
            this.filterRef.emit(this.selectFilterHTML);
            this.property.filterRef = this.selectFilterHTML;
        }
    }

    isOptionDisabled(option: T): boolean {
        const list = this.disabledOptions;
        if (!list || list.length === 0) {
            return false;
        }

        if (typeof list[0] === 'number' || typeof list[0] === 'string') {
            return list.includes(option);
        }

        if (typeof list[0] === 'object') {
            return list.some((disabled) => disabled.id === option.id);
        }

        return false;
    }

    /**
     * Used to filter data based on search input
     */
    filter(name: string = this.searchTextboxControl.value): void {
        this.setSelectedValues();
        if (name === '' && this.property.isServerFilter) {
            this.property.filteredOptions = of(this.selectedValues.map((item) => item));
        } else {
            this.property.Filter(name.toLowerCase(), this.property.options);
        }
        this.property.filteredOptions = this.property.filteredOptions.pipe(
            map((options) => options.filter((option, i) => !this.hideOptions?.includes(option))),
        );
        this.property.filteredOptions.subscribe(() => (this.isSearching = false));
    }

    /**
     * Remove from selected values based on uncheck
     */
    // @ts-expect-error TS7053: implicit any
    selectionChange(event) {
        if (event.isUserInput && event.source.selected === false) {
            this.selectedValues.forEach((value, index) => {
                if (value.name === event.source.value.name && value.id === event.source.value.id) {
                    this.selectedValues.splice(index, 1);
                }
            });
        }
    }

    openedChange() {
        if (isDeferrableFilter(this.property)) {
            this.property.isOpened = true;
            if (this.property.isFilterable) {
                if (this.canMultiSelect(this.property)) {
                    this.selectedValues = [...this.property.Value];
                } else if (isSelectFilter(this.property)) {
                    this.selectedValues = [this.property.Value];
                }
                this.searchTextboxControl.setValue('');
                this.filter();
                const searchInput = fromEvent(this.searchTextBox.nativeElement, 'input').pipe(
                    debounce(() => {
                        this.isSearching = true;
                        return interval(SEARCH_DELAY);
                    }),
                );
                this.subs.push(searchInput.subscribe(() => this.filter()));
                this.searchTextBox.nativeElement.focus();
            }
        }
    }

    closedChange() {
        if (isDeferrableFilter(this.property)) {
            this.property.isOpened = false;
            if (this.property.isFilterable) {
                this.filter('');
                this.subs.forEach((o) => o.unsubscribe());
            }
            if (this.property.isServerFilter) {
                if (this.canMultiSelect(this.property)) {
                    this.selectedValues.forEach((value) => {
                        if (this.canMultiSelect(this.property)) {
                            if (!isMultiOptionSelected(value, this.property.Value as SupportedTypes[])) {
                                this.property.Value.push(value);
                            }
                        }
                    });
                    this.property.filteredOptions = of(this.property.Value);
                } else {
                    this.property.filteredOptions = of([this.property.Value]);
                }
            }
            // This forces a cascade update
            segmentTrack('User Selected From Dropdown', {
                dropdown: this.property.Key,
                selection: Array.isArray(this.property.Value)
                    ? getAllDisplayText(this.property.Value)
                    : getDisplayText(this.property.Value),
                screen: this.router.url,
            });
            this.property.refreshFilter();
            this.valueChanged.emit();
        }
    }

    canMultiSelect(property: SelectFilter<T> | MultiSelectFilter<T>): property is MultiSelectFilter<T> {
        return isMultiSelectFilter(property) || isGroupMultiSelectFilter(property);
    }

    clearSearch() {
        this.searchTextboxControl.setValue('');
        this.filter();
    }

    /**
     * Set selected values to retain the state
     */
    setSelectedValues() {
        if (this.canMultiSelect(this.property)) {
            if (this.property.Value && this.property.Value.length > 0) {
                this.property.Value.forEach((e) => {
                    if (!isMultiOptionSelected<T>(e, this.selectedValues)) {
                        this.selectedValues.push(e);
                    }
                });
            }
            if (this.selectedValues && this.selectedValues.length > 0) {
                this.selectedValues.forEach((value) => {
                    if (this.canMultiSelect(this.property)) {
                        if (!isMultiOptionSelected(value, this.property.Value)) {
                            this.property.Value.push(value);
                        }
                    }
                });
            }
        }
    }

    async keyPress(event: KeyboardEvent) {
        if (event && event.code === SPACE_KEY_CODE) {
            event.stopPropagation();
        }
        if (this.preventTab && event?.code === TAB_KEY_CODE) {
            event.preventDefault();
        }
        if (this.canMultiSelect(this.property)) {
            if (event.code === KEY_A_CODE && event.ctrlKey && this.property.Value && this.property.Value.length) {
                this.subs.push(
                    this.property.filteredOptions.subscribe((options) => {
                        const valuesToRemove: SupportedTypes[] = [];
                        if (this.doesControlContainsAllOptions(options)) {
                            options.forEach((option) => {
                                if (isMultiOptionSelected(option, this.selectedValues)) {
                                    valuesToRemove.push(option);
                                }
                            });
                            valuesToRemove.forEach((value) => {
                                this.selectedValues.splice(
                                    this.selectedValues.findIndex(
                                        (selectedValue) => selectedValue.name === value.name && selectedValue.id === value.id,
                                    ),
                                    1,
                                );
                            });
                        }
                    }),
                );
            }
        }
    }

    doesControlContainsAllOptions(options: T[]): boolean[] {
        return options.map((option) => {
            if (this.canMultiSelect(this.property)) {
                return this.property.Value.includes(option);
            }
        });
    }

    selectAll(event: MatCheckboxChange) {
        if (this.canMultiSelect(this.property)) {
            this.property.SelectAll(event.checked);
            if (!event.checked) {
                this.selectedValues = [];
            }
        }
    }

    toggleGroup(groupName: string, event: MouseEvent): void {
        const target = event.target as HTMLElement;
        if (target.closest('mat-option') || target.closest('.mat-mdc-option')) {
            return;
        }
        event.stopPropagation();
        if (this.collapsedGroups.has(groupName)) {
            this.collapsedGroups.delete(groupName);
        } else {
            this.collapsedGroups.add(groupName);
        }
    }

    isGroupCollapsed(groupName: string): boolean {
        if (this.collapsible && !this.initializedGroups.has(groupName)) {
            this.initializedGroups.add(groupName);
            if (groupName !== new Date().getFullYear().toString()) {
                this.collapsedGroups.add(groupName);
            }
        }
        return this.collapsedGroups.has(groupName);
    }

    ngOnDestroy() {
        this.subs.forEach((o) => o.unsubscribe());
    }

    // @ts-expect-error TS7053: implicit any
    deleteItem(option): void {
        this.optionDeleteButtonClicked.emit(option);
    }

    // The purpose of this function is to determine if an array is an array of political initialization tiers, so we need to use any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getPoliticalOptions(options: any[]): PoliticalInitializationTiers[] {
        return options.filter((item) => item.isPolitical);
    }

    // The purpose of this function is to determine if an array is an array of political initialization tiers, so we need to use any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getDynamicOptions(options: any[]): PoliticalInitializationTiers[] {
        return options.filter((item) => item.isDynamic);
    }

    // The purpose of this function is to determine if an array is an array of political initialization tiers, so we need to use any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getPCodeOptions(options: any[]): PoliticalInitializationTiers[] {
        return options.filter((item) => item.isPCode);
    }

    // The purpose of this function is to determine if an array is an array of political initialization tiers, so we need to use any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    checkPCodeTierAvailability(options: any[]): options is PoliticalInitializationTiers[] {
        return !!options.find((item) => item.isPCode);
    }
}
